/**
 * Utility functions for project-related formatting and calculations
 */

/**
 * Format last activity date as relative time
 * @param {string|null} date - ISO date string
 * @returns {string} - Formatted relative time string
 */
export const formatLastActivity = (date) => {
    if (!date) return 'No activity';
    
    const now = new Date();
    const activityDate = new Date(date);
    const diffMs = now - activityDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
};

/**
 * Calculate completion percentage
 * @param {number} completed - Number of completed tasks
 * @param {number} total - Total number of tasks
 * @returns {number} - Completion percentage (0-100)
 */
export const calculateCompletionPercentage = (completed, total) => {
    if (!total || total === 0) return 0;
    return Math.round((completed / total) * 100);
};

/**
 * Format hours for display
 * @param {number} hours - Total hours
 * @returns {string} - Formatted hours string
 */
export const formatHours = (hours) => {
    if (!hours || hours === 0) return '0h';
    
    const rounded = Math.round(hours * 10) / 10;
    
    if (rounded < 1) {
        const minutes = Math.round(hours * 60);
        return `${minutes}m`;
    }
    
    if (rounded < 10) {
        return `${rounded.toFixed(1)}h`;
    }
    
    return `${Math.round(rounded)}h`;
};

/**
 * Get progress bar color based on completion percentage
 * @param {number} percentage - Completion percentage (0-100)
 * @returns {string} - Hex color code for the progress bar
 */
export const getProgressColor = (percentage) => {
    if (percentage >= 80) return '#059669'; // Darker green
    if (percentage >= 50) return '#d97706'; // Darker amber
    return '#dc2626'; // Darker red
};


